import React, { useState, useEffect, useCallback } from 'react';
import API, { BACKEND_URL } from '../api';
import { useConfirm } from '../components/ConfirmDialog';
import {
  ArrowLeft, HardHat, BookOpen, Calendar, Users, Truck, Package, FileText,
  FilePlus, Sparkles, MessageSquare, ClipboardList, TrendingUp, X, Plus, Trash2,
  Upload, Download, AlertCircle, Camera, MapPin, CheckCircle2, Clock, Euro,
  Cloud, ShieldCheck, AlertTriangle, Edit3, Edit2, UserCheck
} from 'lucide-react';
import CantiereChat from './CantiereChat';
import SALPage from './SALPage';
import MultiClienteSelect from '../components/MultiClienteSelect';

const fmt = (n) => `${(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '-';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : '-';

export default function CantiereWorkspace({ cantiereId, onBack }) {
  const [cantiere, setCantiere] = useState(null);
  const [tab, setTab] = useState('riepilogo');
  const [riepilogo, setRiepilogo] = useState(null);
  const [me, setMe] = useState(null);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showClientiModal, setShowClientiModal] = useState(false);

  const loadCantiere = useCallback(async () => {
    try {
      const r = await API.get(`/api/cantieri/${cantiereId}`);
      setCantiere(r.data);
    } catch (e) {}
  }, [cantiereId]);

  const loadRiepilogo = useCallback(async () => {
    try {
      const r = await API.get(`/api/cantieri/${cantiereId}/riepilogo`);
      setRiepilogo(r.data);
    } catch (e) {}
  }, [cantiereId]);

  useEffect(() => {
    loadCantiere(); loadRiepilogo();
    API.get('/api/auth/me').then(r => setMe(r.data)).catch(() => {});
  }, [loadCantiere, loadRiepilogo]);

  if (!cantiere) return <div className="p-8 text-center text-slate-400">Caricamento...</div>;

  const tabs = [
    { id: 'riepilogo', label: 'Riepilogo', icon: TrendingUp },
    { id: 'diario', label: 'Diario', icon: BookOpen },
    { id: 'calendario', label: 'Calendario', icon: Calendar },
    { id: 'manodopera', label: 'Manodopera', icon: Users },
    { id: 'mezzi', label: 'Mezzi/Noleggi', icon: Truck },
    { id: 'materiali', label: 'Materiali', icon: Package },
    { id: 'ddt', label: 'DDT/Bolle', icon: FilePlus },
    { id: 'documenti', label: 'Documenti', icon: FileText },
    { id: 'extra', label: 'Lavorazioni Extra', icon: Sparkles },
    { id: 'sal', label: 'SAL', icon: ClipboardList },
    // Tab Pagatori: visibile solo se il cantiere ha più di un cliente
    ...((cantiere?.clienti_ids && cantiere.clienti_ids.length >= 2) ? [{ id: 'pagatori', label: 'Pagatori', icon: Euro }] : []),
    { id: 'chat', label: 'Chat AI', icon: MessageSquare },
  ];

  return (
    <div className="animate-fade-in" data-testid="cantiere-workspace">
      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-md p-4 mb-4">
        <button onClick={onBack} className="text-sm text-slate-500 hover:text-primary flex items-center gap-1 mb-2" data-testid="back-to-cantieri">
          <ArrowLeft size={14} /> Torna a tutti i cantieri
        </button>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <HardHat size={32} className="text-primary" />
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight">{cantiere.titolo}</h1>
              <p className="text-xs text-slate-500">{cantiere.codice_cantiere || '—'} · Cliente: {cantiere.cliente_nome || '—'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <StatoCantiereSelect cid={cantiereId} cantiere={cantiere} onChanged={loadCantiere} />
            <div className="text-center">
              <p className="text-[10px] uppercase font-bold text-slate-400">Avanzamento</p>
              <p className="text-2xl font-black text-primary">{cantiere.percentuale_avanzamento || 0}%</p>
            </div>
            <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-primary transition-all" style={{ width: `${cantiere.percentuale_avanzamento || 0}%` }} />
            </div>
            <CreaFatturaBtn cid={cantiereId} cantiere={cantiere} />
          </div>
        </div>

        {/* Team del cantiere */}
        <div className="mt-3 pt-3 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs">
          <div className="flex items-center gap-2 flex-wrap">
            <UserCheck size={14} className="text-slate-400" />
            <span className="font-bold text-slate-600 uppercase tracking-wide">Team:</span>
            {cantiere.responsabile_dettaglio ? (
              <span className="px-2 py-0.5 bg-primary text-primary rounded font-bold">★ {cantiere.responsabile_dettaglio.nome}</span>
            ) : <span className="text-slate-400 italic">nessun responsabile</span>}
            {(cantiere.operatori_dettaglio || []).map(op => (
              <span key={op.id} className="px-2 py-0.5 bg-blue-50 text-blue-800 rounded">{op.nome}</span>
            ))}
            {(!cantiere.operatori_dettaglio || cantiere.operatori_dettaglio.length === 0) && (
              <span className="text-slate-400 italic">nessun operatore</span>
            )}
          </div>
          {me && me.ruolo !== 'operatore' && (
            <button onClick={() => setShowTeamModal(true)} className="ml-auto text-[11px] text-primary hover:underline font-bold flex items-center gap-1" data-testid="edit-team-btn">
              <Edit3 size={12} /> Modifica team
            </button>
          )}
        </div>

        {/* Clienti / Pagatori del cantiere */}
        <div className="mt-3 pt-3 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs">
          <div className="flex items-center gap-2 flex-wrap">
            <Users size={14} className="text-slate-400" />
            <span className="font-bold text-slate-600 uppercase tracking-wide">Clienti/Pagatori:</span>
            {(cantiere.clienti_dettaglio || []).length > 0 ? (
              (cantiere.clienti_dettaglio || []).map((c, idx) => (
                <span key={c.id} className={`px-2 py-0.5 rounded font-bold ${idx === 0 ? 'bg-primary text-primary' : 'bg-blue-50 text-blue-800'}`}>
                  {idx === 0 ? '★ ' : ''}{c.label}
                </span>
              ))
            ) : <span className="text-slate-400 italic">nessun cliente</span>}
          </div>
          {me && me.ruolo !== 'operatore' && (
            <button onClick={() => setShowClientiModal(true)} className="ml-auto text-[11px] text-primary hover:underline font-bold flex items-center gap-1" data-testid="edit-clienti-btn">
              <Edit3 size={12} /> Modifica clienti
            </button>
          )}
        </div>

        {/* Preventivi linkati al cantiere (multi-preventivo) */}
        {(cantiere.preventivi_dettaglio || []).length > 0 && (
          <div className="mt-3 pt-3 border-t border-slate-100 flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4 text-xs" data-testid="cantiere-preventivi-bar">
            <div className="flex items-center gap-2 flex-wrap">
              <FileText size={14} className="text-slate-400" />
              <span className="font-bold text-slate-600 uppercase tracking-wide">Preventivi:</span>
              {(cantiere.preventivi_dettaglio || []).map(p => (
                <span key={p.id}
                  className="px-2 py-0.5 bg-purple-50 text-purple-800 rounded font-bold flex items-center gap-1"
                  title={p.titolo}>
                  {p.numero_preventivo}
                  <span className="text-purple-500 font-mono text-[10px]">€{Math.round(p.totale_finale || 0).toLocaleString('it-IT')}</span>
                  {p.applica_iva === false && <span className="text-[9px] font-bold text-amber-700 bg-amber-100 px-1 rounded">NO IVA</span>}
                  {(p.sconto_globale_pct || 0) > 0 && <span className="text-[9px] font-bold text-red-700 bg-red-100 px-1 rounded">-{p.sconto_globale_pct}%</span>}
                </span>
              ))}
              {(cantiere.preventivi_dettaglio || []).length >= 2 && (
                <span className="text-[10px] font-bold text-purple-600 bg-purple-100 px-2 py-0.5 rounded">
                  {cantiere.preventivi_dettaglio.length} preventivi · SAL aggregato o per singolo
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 mb-4 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-bold whitespace-nowrap transition-all border-b-2 ${tab === t.id ? 'border-primary text-primary' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'riepilogo' && <RiepilogoTab riepilogo={riepilogo} reload={loadRiepilogo} />}
      {tab === 'diario' && <DiarioTab cid={cantiereId} onSaved={loadRiepilogo} />}
      {tab === 'calendario' && <CalendarioTab cid={cantiereId} onSaved={loadRiepilogo} />}
      {tab === 'manodopera' && <ManodoperaTab cid={cantiereId} onSaved={loadRiepilogo} />}
      {tab === 'mezzi' && <MezziTab cid={cantiereId} onSaved={loadRiepilogo} />}
      {tab === 'materiali' && <MaterialiTab cid={cantiereId} onSaved={loadRiepilogo} />}
      {tab === 'ddt' && <DDTTab cid={cantiereId} />}
      {tab === 'documenti' && <DocumentiTab cid={cantiereId} />}
      {tab === 'extra' && <ExtraTab cid={cantiereId} cantiere={cantiere} onSaved={loadRiepilogo} />}
      {tab === 'sal' && <SALPage cantiereId={cantiereId} cantiereTitolo={cantiere.titolo || cantiere.codice_cantiere} />}
      {tab === 'pagatori' && <PagatoriTab cid={cantiereId} cantiere={cantiere} />}
      {tab === 'chat' && <CantiereChat cantiereId={cantiereId} />}

      {showTeamModal && (
        <TeamModal
          cantiere={cantiere}
          onClose={() => setShowTeamModal(false)}
          onSaved={() => { setShowTeamModal(false); loadCantiere(); }}
        />
      )}

      {showClientiModal && (
        <ClientiCantiereModal
          cantiere={cantiere}
          onClose={() => setShowClientiModal(false)}
          onSaved={() => { setShowClientiModal(false); loadCantiere(); }}
        />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// TEAM MODAL - Assegnazione operatori e responsabile
// ════════════════════════════════════════════════════════════════
function TeamModal({ cantiere, onClose, onSaved }) {
  const [dipendenti, setDipendenti] = useState([]);
  const [operatori, setOperatori] = useState(cantiere.operatori_assegnati || []);
  const [responsabile, setResponsabile] = useState(cantiere.responsabile_id || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    API.get('/api/dipendenti').then(r => setDipendenti(r.data)).catch(e => setErr('Impossibile caricare dipendenti'));
  }, []);

  const toggle = (id) => {
    setOperatori(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const save = async () => {
    setSaving(true);
    setErr('');
    try {
      await API.put(`/api/cantieri/${cantiere._id || cantiere.id}/team`, {
        operatori_assegnati: operatori,
        responsabile_id: responsabile,
      });
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const operatoriList = dipendenti.filter(d => d.ruolo === 'operatore');
  const responsabiliList = dipendenti.filter(d => d.ruolo === 'admin' || d.ruolo === 'impiegato');

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-md w-full sm:max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl" data-testid="team-modal">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <h2 className="text-lg font-extrabold flex items-center gap-2"><UserCheck size={18} /> Team del cantiere</h2>
            <p className="text-xs text-slate-500">Assegna responsabile e operatori per filtrarne la visibilita</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="close-team-modal"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-5">
          {err && <div className="p-2 bg-red-50 border border-red-200 rounded text-red-700 text-xs">{err}</div>}

          <div>
            <label className="text-xs font-bold uppercase text-slate-600 mb-1 block">Responsabile (admin/impiegato)</label>
            <select value={responsabile} onChange={e => setResponsabile(e.target.value)} className="w-full border border-slate-200 rounded px-3 py-2 text-sm" data-testid="select-responsabile">
              <option value="">-- nessuno --</option>
              {responsabiliList.map(d => (
                <option key={d.id} value={d.id}>{d.nome} ({d.mansione || d.ruolo})</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-bold uppercase text-slate-600 mb-1 block">Operatori assegnati</label>
            <p className="text-[11px] text-slate-500 mb-2">Seleziona gli operatori che dovranno vedere e gestire questo cantiere (anche tramite Geppo).</p>
            <div className="space-y-1 max-h-64 overflow-y-auto border border-slate-200 rounded p-2">
              {operatoriList.length === 0 && <p className="text-slate-400 text-sm py-2 text-center">Nessun operatore disponibile. Crealo dalla pagina Dipendenti.</p>}
              {operatoriList.map(d => (
                <label key={d.id} className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer" data-testid={`operatore-checkbox-${d.id}`}>
                  <input type="checkbox" checked={operatori.includes(d.id)} onChange={() => toggle(d.id)} className="accent-primary" />
                  <span className="flex-1 text-sm">{d.nome}</span>
                  <span className="text-[11px] text-slate-500">{d.mansione || '-'}</span>
                  {d.costo_orario > 0 && <span className="text-[11px] font-mono text-slate-400">{d.costo_orario.toFixed(0)}€/h</span>}
                </label>
              ))}
            </div>
          </div>

          <div className="flex gap-2 justify-end pt-2">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-slate-200 rounded hover:bg-slate-50" data-testid="cancel-team">Annulla</button>
            <button onClick={save} disabled={saving} className="px-4 py-2 text-sm bg-primary text-white font-bold rounded hover:bg-primary-hover disabled:opacity-50" data-testid="save-team">
              {saving ? 'Salvataggio...' : 'Salva'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// RIEPILOGO TAB
// ════════════════════════════════════════════════════════════════
function RiepilogoTab({ riepilogo, reload }) {
  if (!riepilogo) return <div className="text-slate-400 py-6 text-center">Caricamento...</div>;
  const { manodopera, mezzi, materiali, stats, eventi_futuri, extra, economia, sal } = riepilogo;
  return (
    <div className="space-y-4" data-testid="riepilogo-tab">
      {/* Economia */}
      <div className="bg-white border border-slate-200 rounded-md p-5">
        <h3 className="text-sm font-bold uppercase text-slate-600 mb-3 flex items-center gap-2"><Euro size={14} /> Economia</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <Metric label="Preventivato" value={fmt(economia.preventivato)} color="text-blue-600" />
          <Metric label="Extra consuntivato" value={fmt(economia.extra_ricavo || 0)} color="text-purple-600" sub={(economia.extra_ricavo || 0) > 0 ? `${riepilogo.extra_consuntivato?.n_completate || 0} extra` : ''} />
          <Metric label="Ricavo totale" value={fmt(economia.ricavo_totale || economia.preventivato)} color="text-emerald-700" />
          <Metric label="Costi" value={fmt(economia.consuntivo)} color="text-primary" sub={economia.consuntivo_extra > 0 ? `+ ${fmt(economia.consuntivo_extra)} costi extra` : ''} />
          <Metric label="Margine" value={fmt(economia.margine)} color={economia.margine >= 0 ? 'text-green-600' : 'text-red-600'} />
          <Metric label="Margine %" value={`${economia.margine_pct.toFixed(1)}%`} color={economia.margine_pct >= 0 ? 'text-green-600' : 'text-red-600'} />
        </div>
      </div>

      {/* Costi cantiere */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <CostCard title="Manodopera" icon={Users} value={fmt(manodopera.costo_totale)} subtitle={`${manodopera.ore_totali.toFixed(1)} h - ${manodopera.n_timbrature} timbrature`} color="bg-blue-50 border-blue-200" />
        <CostCard title="Mezzi/Noleggi" icon={Truck} value={fmt(mezzi.costo_totale)} subtitle={`${mezzi.n_voci} voci${mezzi.costo_extra_richieste > 0 ? ` (extra: ${fmt(mezzi.costo_extra_richieste)})` : ''}`} color="bg-primary border-primary" />
        <CostCard title="Materiali" icon={Package} value={fmt(materiali.costo_totale)} subtitle={`${materiali.n_voci} voci${materiali.costo_extra_richieste > 0 ? ` (extra: ${fmt(materiali.costo_extra_richieste)})` : ''}`} color="bg-green-50 border-green-200" />
      </div>

      {/* Manodopera per dipendente */}
      {manodopera.per_dipendente.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <h3 className="text-sm font-bold uppercase text-slate-600 mb-2">Ore per dipendente</h3>
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500"><tr><th className="text-left py-1">Dipendente</th><th className="text-right py-1">Ore</th><th className="text-right py-1">Costo</th></tr></thead>
            <tbody>
              {manodopera.per_dipendente.map((d, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="py-1.5">{d.dipendente}</td>
                  <td className="py-1.5 text-right font-mono">{d.ore.toFixed(1)} h</td>
                  <td className="py-1.5 text-right font-mono font-bold">{fmt(d.costo)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Eventi prossimi */}
      {eventi_futuri.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <h3 className="text-sm font-bold uppercase text-slate-600 mb-2 flex items-center gap-1"><Calendar size={14} /> Prossimi eventi</h3>
          <div className="space-y-1">
            {eventi_futuri.map(e => (
              <div key={e.id} className="flex items-center gap-2 text-sm py-1 border-b border-slate-100">
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded font-bold">{e.tipo}</span>
                <span className="font-semibold flex-1">{e.titolo}</span>
                <span className="text-xs text-slate-500">{fmtDateTime(e.data_inizio)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MiniStat label="Annotazioni diario" value={stats.n_diario} />
        <MiniStat label="DDT/Bolle" value={stats.n_ddt} />
        <MiniStat label="Documenti" value={stats.n_documenti} />
        <MiniStat label="Eventi prossimi" value={stats.n_eventi_futuri} />
      </div>

      {/* SAL */}
      {sal.stati.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <h3 className="text-sm font-bold uppercase text-slate-600 mb-2 flex items-center gap-1"><ClipboardList size={14} /> SAL</h3>
          <div className="flex flex-wrap gap-2 text-sm">
            {sal.stati.map((s, i) => (
              <span key={i} className="px-3 py-1.5 bg-slate-100 rounded">
                <b>{s.stato}</b>: {s.count} ({fmt(s.importo)})
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, color, sub }) {
  return (
    <div className="bg-slate-50 rounded p-3">
      <p className="text-[10px] uppercase font-bold text-slate-400 mb-0.5">{label}</p>
      <p className={`text-base font-black ${color}`}>{value}</p>
      {sub && <p className="text-[10px] text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function CostCard({ title, icon: Icon, value, subtitle, color }) {
  return (
    <div className={`border rounded-md p-4 ${color}`}>
      <Icon size={20} className="mb-1" />
      <p className="text-[10px] uppercase font-bold text-slate-600 mb-0.5">{title}</p>
      <p className="text-2xl font-black">{value}</p>
      <p className="text-xs text-slate-500 mt-1">{subtitle}</p>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded p-3 text-center">
      <p className="text-2xl font-black text-primary">{value}</p>
      <p className="text-[10px] uppercase font-bold text-slate-400">{label}</p>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// DIARIO TAB
// ════════════════════════════════════════════════════════════════
function DiarioTab({ cid, onSaved }) {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ tipo: 'annotazione', titolo: '', testo: '', meteo: '', percentuale_aggiornata: '', dpi_ok: true });

  const load = useCallback(() => API.get(`/api/cantieri/${cid}/diario`).then(r => setItems(r.data)), [cid]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      const payload = { ...form };
      if (payload.percentuale_aggiornata === '') delete payload.percentuale_aggiornata;
      await API.post(`/api/cantieri/${cid}/diario`, payload);
      setShowForm(false);
      setForm({ tipo: 'annotazione', titolo: '', testo: '', meteo: '', percentuale_aggiornata: '', dpi_ok: true });
      load(); onSaved?.();
    } catch (e) { alert('Errore: ' + (e.response?.data?.detail || e.message)); }
  };

  const del = async (id) => {
    if (!await confirm({ title: 'Eliminare nota diario?', message: 'L\'annotazione verrà rimossa dal diario di cantiere.', confirmLabel: 'Elimina', variant: 'danger' })) return;
    await API.delete(`/api/cantieri/${cid}/diario/${id}`);
    load(); onSaved?.();
  };

  const TIPO_LABEL = {
    annotazione: { label: 'Annotazione', icon: BookOpen, color: 'bg-slate-100 text-slate-700' },
    avanzamento: { label: 'Avanzamento', icon: TrendingUp, color: 'bg-green-100 text-green-700' },
    problema: { label: 'Problema', icon: AlertTriangle, color: 'bg-red-100 text-red-700' },
    sicurezza: { label: 'Sicurezza', icon: ShieldCheck, color: 'bg-yellow-100 text-yellow-700' },
    foto: { label: 'Foto', icon: Camera, color: 'bg-purple-100 text-purple-700' },
    meteo: { label: 'Meteo', icon: Cloud, color: 'bg-blue-100 text-blue-700' },
  };

  return (
    <div data-testid="diario-tab">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-bold flex items-center gap-2"><BookOpen size={16} /> Diario di Cantiere ({items.length})</h3>
        <button onClick={() => setShowForm(true)} data-testid="add-diario-btn"
          className="px-3 py-1.5 bg-primary text-white rounded-md text-sm font-bold flex items-center gap-1 hover:bg-primary/90">
          <Plus size={14} /> Nuova nota
        </button>
      </div>

      {showForm && (
        <div className="bg-slate-50 border border-slate-200 rounded-md p-4 mb-3 space-y-3" data-testid="diario-form">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase">Tipo</label>
              <select value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value })} className="input w-full">
                {Object.entries(TIPO_LABEL).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase">Meteo</label>
              <select value={form.meteo} onChange={e => setForm({ ...form, meteo: e.target.value })} className="input w-full">
                <option value="">--</option>
                <option value="sereno">Sereno</option>
                <option value="nuvoloso">Nuvoloso</option>
                <option value="pioggia">Pioggia</option>
                <option value="neve">Neve</option>
              </select>
            </div>
          </div>
          <input placeholder="Titolo" className="input w-full" value={form.titolo} onChange={e => setForm({ ...form, titolo: e.target.value })} />
          <textarea placeholder="Note dettagliate..." rows={3} className="input w-full" value={form.testo} onChange={e => setForm({ ...form, testo: e.target.value })} />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase">Avanzamento %</label>
              <input type="number" min="0" max="100" placeholder="opzionale" className="input w-full" value={form.percentuale_aggiornata} onChange={e => setForm({ ...form, percentuale_aggiornata: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 mt-5">
              <input type="checkbox" checked={form.dpi_ok} onChange={e => setForm({ ...form, dpi_ok: e.target.checked })} />
              <span className="text-sm">DPI in regola</span>
            </label>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowForm(false)} className="px-3 py-1.5 border border-slate-300 rounded text-sm">Annulla</button>
            <button onClick={submit} className="px-4 py-1.5 bg-primary text-white rounded text-sm font-bold" data-testid="save-diario">Salva</button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {items.length === 0 && <p className="text-slate-400 text-center py-6 text-sm">Nessuna annotazione. Aggiungi la prima!</p>}
        {items.map(it => {
          const t = TIPO_LABEL[it.tipo] || TIPO_LABEL.annotazione;
          const Icon = t.icon;
          return (
            <div key={it.id} className="bg-white border border-slate-200 rounded p-3 hover:shadow-sm">
              <div className="flex items-start gap-3">
                <span className={`p-1.5 rounded ${t.color} flex-shrink-0`}><Icon size={14} /></span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${t.color}`}>{t.label}</span>
                    {it.titolo && <b className="text-sm">{it.titolo}</b>}
                    {it.percentuale_aggiornata != null && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-bold">+{it.percentuale_aggiornata}%</span>
                    )}
                    {it.meteo && <span className="text-xs text-slate-500">☁ {it.meteo}</span>}
                  </div>
                  {it.testo && <p className="text-sm text-slate-700 whitespace-pre-wrap">{it.testo}</p>}
                  <p className="text-[10px] text-slate-400 mt-1">{fmtDateTime(it.data_ora)} · {it.autore_nome}</p>
                </div>
                <button onClick={() => del(it.id)} className="text-red-500 hover:bg-red-50 p-1 rounded" data-testid={`del-diario-${it.id}`}><Trash2 size={14} /></button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// CALENDARIO TAB
// ════════════════════════════════════════════════════════════════
function CalendarioTab({ cid, onSaved }) {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { titolo: '', descrizione: '', data_inizio: '', data_fine: '', tipo: 'sopralluogo', luogo: '', stato: 'programmato', all_day: false };
  const [form, setForm] = useState(empty);

  const load = useCallback(() => API.get(`/api/cantieri/${cid}/eventi`).then(r => setItems(r.data)), [cid]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      const payload = { ...form };
      if (editing) await API.put(`/api/cantieri/${cid}/eventi/${editing.id}`, payload);
      else await API.post(`/api/cantieri/${cid}/eventi`, payload);
      setShowForm(false); setEditing(null); setForm(empty);
      load(); onSaved?.();
    } catch (e) { alert('Errore: ' + (e.response?.data?.detail || e.message)); }
  };
  const del = async (id) => { if (!await confirm({ title: 'Eliminare evento?', message: 'L\'evento verrà rimosso dal calendario cantiere.', confirmLabel: 'Elimina', variant: 'danger' })) return; await API.delete(`/api/cantieri/${cid}/eventi/${id}`); load(); onSaved?.(); };
  const edit = (e) => {
    setEditing(e);
    setForm({
      titolo: e.titolo || '',
      descrizione: e.descrizione || '',
      data_inizio: e.data_inizio ? new Date(e.data_inizio).toISOString().slice(0, 16) : '',
      data_fine: e.data_fine ? new Date(e.data_fine).toISOString().slice(0, 16) : '',
      tipo: e.tipo || 'sopralluogo',
      luogo: e.luogo || '',
      stato: e.stato || 'programmato',
      all_day: !!e.all_day,
    });
    setShowForm(true);
  };

  const TIPO_COLOR = { sopralluogo: 'bg-blue-100 text-blue-700', scadenza: 'bg-red-100 text-red-700', consegna: 'bg-green-100 text-green-700', riunione: 'bg-purple-100 text-purple-700', ispezione: 'bg-yellow-100 text-yellow-700', altro: 'bg-slate-100 text-slate-700' };
  const STATO_COLOR = { programmato: 'bg-blue-100 text-blue-700', completato: 'bg-green-100 text-green-700', annullato: 'bg-slate-100 text-slate-500' };

  return (
    <div data-testid="calendario-tab">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-bold flex items-center gap-2"><Calendar size={16} /> Calendario eventi ({items.length})</h3>
        <button onClick={() => { setEditing(null); setForm(empty); setShowForm(true); }} className="px-3 py-1.5 bg-primary text-white rounded-md text-sm font-bold flex items-center gap-1" data-testid="add-evento-btn">
          <Plus size={14} /> Nuovo evento
        </button>
      </div>

      {showForm && (
        <div className="bg-slate-50 border rounded-md p-4 mb-3 space-y-3" data-testid="evento-form">
          <input placeholder="Titolo evento" className="input w-full" value={form.titolo} onChange={e => setForm({ ...form, titolo: e.target.value })} />
          <textarea placeholder="Descrizione" rows={2} className="input w-full" value={form.descrizione} onChange={e => setForm({ ...form, descrizione: e.target.value })} />
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-xs font-bold uppercase text-slate-500">Inizio</label>
              <input type="datetime-local" className="input w-full" value={form.data_inizio} onChange={e => setForm({ ...form, data_inizio: e.target.value })} /></div>
            <div><label className="text-xs font-bold uppercase text-slate-500">Fine</label>
              <input type="datetime-local" className="input w-full" value={form.data_fine} onChange={e => setForm({ ...form, data_fine: e.target.value })} /></div>
            <div><label className="text-xs font-bold uppercase text-slate-500">Tipo</label>
              <select value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value })} className="input w-full">
                <option value="sopralluogo">Sopralluogo</option>
                <option value="scadenza">Scadenza</option>
                <option value="consegna">Consegna</option>
                <option value="riunione">Riunione</option>
                <option value="ispezione">Ispezione</option>
                <option value="altro">Altro</option>
              </select></div>
            <div><label className="text-xs font-bold uppercase text-slate-500">Stato</label>
              <select value={form.stato} onChange={e => setForm({ ...form, stato: e.target.value })} className="input w-full">
                <option value="programmato">Programmato</option>
                <option value="completato">Completato</option>
                <option value="annullato">Annullato</option>
              </select></div>
          </div>
          <input placeholder="Luogo" className="input w-full" value={form.luogo} onChange={e => setForm({ ...form, luogo: e.target.value })} />
          <div className="flex gap-2 justify-end">
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="px-3 py-1.5 border rounded text-sm">Annulla</button>
            <button onClick={submit} className="px-4 py-1.5 bg-primary text-white rounded text-sm font-bold" data-testid="save-evento">Salva</button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {items.length === 0 && <p className="text-slate-400 text-center py-6 text-sm">Nessun evento programmato.</p>}
        {items.map(e => (
          <div key={e.id} className="bg-white border border-slate-200 rounded p-3">
            <div className="flex items-start gap-3">
              <Calendar size={20} className="text-primary flex-shrink-0 mt-1" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <b>{e.titolo}</b>
                  <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${TIPO_COLOR[e.tipo] || ''}`}>{e.tipo}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${STATO_COLOR[e.stato] || ''}`}>{e.stato}</span>
                </div>
                <p className="text-xs text-slate-500">{fmtDateTime(e.data_inizio)} → {fmtDateTime(e.data_fine)}</p>
                {e.luogo && <p className="text-xs text-slate-500 flex items-center gap-1 mt-1"><MapPin size={10} />{e.luogo}</p>}
                {e.descrizione && <p className="text-sm mt-1">{e.descrizione}</p>}
              </div>
              <div className="flex flex-col gap-1">
                <button onClick={() => edit(e)} className="text-blue-500 hover:bg-blue-50 p-1 rounded"><Edit3 size={14} /></button>
                <button onClick={() => del(e.id)} className="text-red-500 hover:bg-red-50 p-1 rounded"><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// MANODOPERA TAB
// ════════════════════════════════════════════════════════════════
function ManodoperaTab({ cid, onSaved }) {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const empty = { dipendente_id: '', dipendente_nome: '', mansione: '', data: new Date().toISOString().slice(0, 10), ore_totali: 8, costo_orario: 25, note: '' };
  const [form, setForm] = useState(empty);

  const load = useCallback(() => API.get(`/api/cantieri/${cid}/manodopera`).then(r => setItems(r.data)), [cid]);
  useEffect(() => { load(); API.get('/api/utenti').then(r => setUsers(r.data || [])).catch(() => {}); }, [load]);

  const onSelectDip = (id) => {
    const u = users.find(x => x.id === id);
    if (u) {
      setForm(f => ({
        ...f, dipendente_id: id,
        dipendente_nome: `${u.nome || ''} ${u.cognome || ''}`.trim() || u.email,
        mansione: u.mansione || f.mansione,
        costo_orario: u.costo_orario || 25,
      }));
    }
  };

  const submit = async () => {
    try {
      const ore = parseFloat(form.ore_totali) || 0;
      if (ore <= 0) { alert('Inserisci le ore lavorate'); return; }
      await API.post(`/api/cantieri/${cid}/manodopera`, { ...form, ore_totali: ore });
      setShowForm(false); setForm(empty);
      load(); onSaved?.();
    } catch (e) { alert('Errore: ' + (e.response?.data?.detail || e.message)); }
  };
  const del = async (id) => { if (!await confirm({ title: 'Eliminare voce manodopera?', message: 'Le ore registrate verranno cancellate dal cantiere.', confirmLabel: 'Elimina', variant: 'danger' })) return; await API.delete(`/api/cantieri/${cid}/manodopera/${id}`); load(); onSaved?.(); };

  const totOre = items.reduce((s, x) => s + (x.ore_totali || 0), 0);
  const totCosto = items.reduce((s, x) => s + (x.costo_totale || 0), 0);

  return (
    <div data-testid="manodopera-tab">
      <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
        <h3 className="font-bold flex items-center gap-2"><Users size={16} /> Manodopera ({items.length})</h3>
        <div className="flex items-center gap-3">
          <span className="text-xs"><b>{totOre.toFixed(1)} h</b> · {fmt(totCosto)}</span>
          <button onClick={() => setShowForm(true)} className="px-3 py-1.5 bg-primary text-white rounded-md text-sm font-bold flex items-center gap-1" data-testid="add-manodopera-btn">
            <Plus size={14} /> Timbra ore
          </button>
        </div>
      </div>

      {showForm && (
        <div className="bg-slate-50 border rounded-md p-4 mb-3 space-y-3" data-testid="manodopera-form">
          <select className="input w-full" value={form.dipendente_id} onChange={e => onSelectDip(e.target.value)}>
            <option value="">-- Seleziona dipendente --</option>
            {users.filter(u => u.attivo).map(u => <option key={u.id} value={u.id}>{u.nome} {u.cognome} ({u.ruolo}) - {u.costo_orario || 25} EUR/h</option>)}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="Mansione" className="input" value={form.mansione} onChange={e => setForm({ ...form, mansione: e.target.value })} />
            <input type="date" className="input" value={form.data} onChange={e => setForm({ ...form, data: e.target.value })} />
            <input type="number" step="0.5" placeholder="Ore lavorate" className="input" value={form.ore_totali} onChange={e => setForm({ ...form, ore_totali: e.target.value })} data-testid="ore-totali" />
            <input type="number" step="0.5" placeholder="Costo orario" className="input" value={form.costo_orario} onChange={e => setForm({ ...form, costo_orario: e.target.value })} />
          </div>
          <textarea placeholder="Note" rows={2} className="input w-full" value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowForm(false)} className="px-3 py-1.5 border rounded text-sm">Annulla</button>
            <button onClick={submit} className="px-4 py-1.5 bg-primary text-white rounded text-sm font-bold" data-testid="save-manodopera">Salva</button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100">
            <tr>
              <th className="text-left p-2">Data</th>
              <th className="text-left p-2">Dipendente</th>
              <th className="text-left p-2">Mansione</th>
              <th className="text-right p-2">Ore</th>
              <th className="text-right p-2">EUR/h</th>
              <th className="text-right p-2">Costo</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id} className="border-t hover:bg-slate-50">
                <td className="p-2 text-xs">{fmtDate(it.data)}</td>
                <td className="p-2 font-semibold">{it.dipendente_nome}</td>
                <td className="p-2 text-xs">{it.mansione}</td>
                <td className="p-2 text-right font-mono">{(it.ore_totali || 0).toFixed(1)}</td>
                <td className="p-2 text-right font-mono">{(it.costo_orario || 0).toFixed(2)}</td>
                <td className="p-2 text-right font-mono font-bold">{fmt(it.costo_totale)}</td>
                <td className="p-2"><button onClick={() => del(it.id)} className="text-red-500 hover:bg-red-50 p-1 rounded"><Trash2 size={12} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <p className="text-slate-400 text-center py-6 text-sm">Nessuna timbratura.</p>}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// MEZZI/NOLEGGI TAB
// ════════════════════════════════════════════════════════════════
function MezziTab({ cid, onSaved }) {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const empty = { descrizione: '', tipo: 'noleggio', fornitore_label: '', data_inizio: new Date().toISOString().slice(0, 10), data_fine: '', quantita: 1, unita_misura: 'giorno', costo_unitario: 0, extra_richiesta: false, note: '' };
  const [form, setForm] = useState(empty);

  const load = useCallback(() => API.get(`/api/cantieri/${cid}/mezzi`).then(r => setItems(r.data)), [cid]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      await API.post(`/api/cantieri/${cid}/mezzi`, form);
      setShowForm(false); setForm(empty); load(); onSaved?.();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const del = async (id) => { if (!await confirm({ title: 'Eliminare mezzo/attrezzatura?', message: 'La voce verrà rimossa dai costi del cantiere.', confirmLabel: 'Elimina', variant: 'danger' })) return; await API.delete(`/api/cantieri/${cid}/mezzi/${id}`); load(); onSaved?.(); };

  const tot = items.reduce((s, x) => s + (x.costo_totale || 0), 0);

  return (
    <div data-testid="mezzi-tab">
      <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
        <h3 className="font-bold flex items-center gap-2"><Truck size={16} /> Mezzi e Noleggi ({items.length})</h3>
        <div className="flex items-center gap-3">
          <span className="text-xs">Totale: <b>{fmt(tot)}</b></span>
          <button onClick={() => setShowForm(true)} className="px-3 py-1.5 bg-primary text-white rounded text-sm font-bold flex items-center gap-1" data-testid="add-mezzo-btn"><Plus size={14} /> Nuovo</button>
        </div>
      </div>

      {showForm && (
        <div className="bg-slate-50 border rounded-md p-4 mb-3 space-y-3">
          <input placeholder="Descrizione mezzo (es. Escavatore CAT 320, Ponteggio mt 300)" className="input w-full" value={form.descrizione} onChange={e => setForm({ ...form, descrizione: e.target.value })} />
          <div className="grid grid-cols-2 gap-2">
            <select className="input" value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value })}>
              <option value="interno">Interno (mezzo aziendale)</option>
              <option value="noleggio">Noleggio esterno</option>
            </select>
            <input placeholder="Fornitore (se noleggio)" className="input" value={form.fornitore_label} onChange={e => setForm({ ...form, fornitore_label: e.target.value })} />
            <input type="date" className="input" value={form.data_inizio} onChange={e => setForm({ ...form, data_inizio: e.target.value })} />
            <input type="date" className="input" value={form.data_fine} onChange={e => setForm({ ...form, data_fine: e.target.value })} />
            <input type="number" step="0.5" placeholder="Quantita" className="input" value={form.quantita} onChange={e => setForm({ ...form, quantita: e.target.value })} />
            <select className="input" value={form.unita_misura} onChange={e => setForm({ ...form, unita_misura: e.target.value })}>
              <option value="ore">Ore</option>
              <option value="giorno">Giorni</option>
              <option value="settimana">Settimane</option>
              <option value="mese">Mesi</option>
              <option value="corpo">A corpo</option>
            </select>
            <input type="number" step="0.01" placeholder="Costo per UM" className="input col-span-2" value={form.costo_unitario} onChange={e => setForm({ ...form, costo_unitario: e.target.value })} />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.extra_richiesta} onChange={e => setForm({ ...form, extra_richiesta: e.target.checked })} />
            Lavorazione extra richiesta dal cliente in corso d'opera
          </label>
          <textarea placeholder="Note" rows={2} className="input w-full" value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowForm(false)} className="px-3 py-1.5 border rounded text-sm">Annulla</button>
            <button onClick={submit} className="px-4 py-1.5 bg-primary text-white rounded text-sm font-bold">Salva</button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {items.map(it => (
          <div key={it.id} className="bg-white border rounded p-3 flex items-start gap-3">
            <Truck size={18} className={it.tipo === 'noleggio' ? 'text-primary' : 'text-blue-500'} />
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <b className="text-sm">{it.descrizione}</b>
                <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-slate-100">{it.tipo}</span>
                {it.extra_richiesta && <span className="text-[10px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded font-bold">EXTRA</span>}
              </div>
              <p className="text-xs text-slate-500">{it.fornitore_label || '—'} · {fmtDate(it.data_inizio)}{it.data_fine ? ` - ${fmtDate(it.data_fine)}` : ''}</p>
              <p className="text-xs">{it.quantita} {it.unita_misura} × {fmt(it.costo_unitario)} = <b>{fmt(it.costo_totale)}</b></p>
              {it.note && <p className="text-xs text-slate-400 italic">{it.note}</p>}
            </div>
            <button onClick={() => del(it.id)} className="text-red-500 hover:bg-red-50 p-1 rounded"><Trash2 size={14} /></button>
          </div>
        ))}
        {items.length === 0 && <p className="text-slate-400 text-center py-6 text-sm">Nessun mezzo registrato.</p>}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// MATERIALI TAB
// ════════════════════════════════════════════════════════════════
function MaterialiTab({ cid, onSaved }) {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const empty = { descrizione: '', codice_articolo: '', fornitore_label: '', data: new Date().toISOString().slice(0, 10), quantita: 1, unita_misura: 'pz', prezzo_unitario: 0, extra_richiesta: false, note: '' };
  const [form, setForm] = useState(empty);

  const load = useCallback(() => API.get(`/api/cantieri/${cid}/materiali`).then(r => setItems(r.data)), [cid]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      await API.post(`/api/cantieri/${cid}/materiali`, form);
      setShowForm(false); setForm(empty); load(); onSaved?.();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const del = async (id) => { if (!await confirm({ title: 'Eliminare materiale?', message: 'La voce verrà rimossa dai costi materiali del cantiere.', confirmLabel: 'Elimina', variant: 'danger' })) return; await API.delete(`/api/cantieri/${cid}/materiali/${id}`); load(); onSaved?.(); };

  const tot = items.reduce((s, x) => s + (x.costo_totale || 0), 0);

  return (
    <div data-testid="materiali-tab">
      <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
        <h3 className="font-bold flex items-center gap-2"><Package size={16} /> Materiali ({items.length})</h3>
        <div className="flex items-center gap-3">
          <span className="text-xs">Totale: <b>{fmt(tot)}</b></span>
          <button onClick={() => setShowForm(true)} className="px-3 py-1.5 bg-primary text-white rounded text-sm font-bold flex items-center gap-1" data-testid="add-materiale-btn"><Plus size={14} /> Nuovo</button>
        </div>
      </div>

      {showForm && (
        <div className="bg-slate-50 border rounded-md p-4 mb-3 space-y-3">
          <input placeholder="Descrizione materiale" className="input w-full" value={form.descrizione} onChange={e => setForm({ ...form, descrizione: e.target.value })} />
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="Codice articolo" className="input" value={form.codice_articolo} onChange={e => setForm({ ...form, codice_articolo: e.target.value })} />
            <input placeholder="Fornitore" className="input" value={form.fornitore_label} onChange={e => setForm({ ...form, fornitore_label: e.target.value })} />
            <input type="date" className="input" value={form.data} onChange={e => setForm({ ...form, data: e.target.value })} />
            <input placeholder="UM (pz, kg, mc, mq, ml...)" className="input" value={form.unita_misura} onChange={e => setForm({ ...form, unita_misura: e.target.value })} />
            <input type="number" step="0.5" placeholder="Quantita" className="input" value={form.quantita} onChange={e => setForm({ ...form, quantita: e.target.value })} />
            <input type="number" step="0.01" placeholder="Prezzo unitario" className="input" value={form.prezzo_unitario} onChange={e => setForm({ ...form, prezzo_unitario: e.target.value })} />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.extra_richiesta} onChange={e => setForm({ ...form, extra_richiesta: e.target.checked })} />
            Materiale extra richiesto dal cliente
          </label>
          <textarea placeholder="Note" rows={2} className="input w-full" value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowForm(false)} className="px-3 py-1.5 border rounded text-sm">Annulla</button>
            <button onClick={submit} className="px-4 py-1.5 bg-primary text-white rounded text-sm font-bold">Salva</button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100">
            <tr>
              <th className="text-left p-2">Data</th>
              <th className="text-left p-2">Descrizione</th>
              <th className="text-left p-2">Fornitore</th>
              <th className="text-right p-2">Q.tà</th>
              <th className="text-right p-2">EUR/UM</th>
              <th className="text-right p-2">Totale</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id} className="border-t hover:bg-slate-50">
                <td className="p-2 text-xs">{fmtDate(it.data)}</td>
                <td className="p-2"><div className="font-semibold">{it.descrizione}</div>{it.codice_articolo && <div className="text-[10px] text-slate-400 font-mono">{it.codice_articolo}</div>}{it.extra_richiesta && <span className="text-[9px] bg-purple-100 text-purple-700 px-1 rounded font-bold">EXTRA</span>}</td>
                <td className="p-2 text-xs">{it.fornitore_label}</td>
                <td className="p-2 text-right font-mono">{it.quantita} {it.unita_misura}</td>
                <td className="p-2 text-right font-mono">{fmt(it.prezzo_unitario)}</td>
                <td className="p-2 text-right font-mono font-bold">{fmt(it.costo_totale)}</td>
                <td className="p-2"><button onClick={() => del(it.id)} className="text-red-500 hover:bg-red-50 p-1 rounded"><Trash2 size={12} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <p className="text-slate-400 text-center py-6 text-sm">Nessun materiale registrato.</p>}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// DDT/BOLLE TAB (upload only)
// ════════════════════════════════════════════════════════════════
function DDTTab({ cid }) {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ numero: '', data: new Date().toISOString().slice(0, 10), fornitore_label: '', totale: '', note: '' });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const API_URL = BACKEND_URL;
  const load = useCallback(() => API.get(`/api/cantieri/${cid}/ddt`).then(r => setItems(r.data)), [cid]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!file) { alert('Seleziona un file da caricare'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('numero', form.numero);
      fd.append('data', form.data);
      fd.append('fornitore_label', form.fornitore_label);
      fd.append('totale', form.totale || '0');
      fd.append('note', form.note);
      await API.post(`/api/cantieri/${cid}/ddt`, fd);
      setShowForm(false); setFile(null); setForm({ numero: '', data: new Date().toISOString().slice(0, 10), fornitore_label: '', totale: '', note: '' });
      load();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
    setUploading(false);
  };
  const del = async (id) => { if (!await confirm({ title: 'Eliminare DDT?', message: 'Il documento di trasporto verrà rimosso dal cantiere.', confirmLabel: 'Elimina', variant: 'danger' })) return; await API.delete(`/api/cantieri/${cid}/ddt/${id}`); load(); };

  return (
    <div data-testid="ddt-tab">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-bold flex items-center gap-2"><FilePlus size={16} /> DDT / Bolle ({items.length})</h3>
        <button onClick={() => setShowForm(true)} className="px-3 py-1.5 bg-primary text-white rounded text-sm font-bold flex items-center gap-1" data-testid="add-ddt-btn">
          <Upload size={14} /> Carica DDT
        </button>
      </div>

      {showForm && (
        <div className="bg-slate-50 border rounded-md p-4 mb-3 space-y-3">
          <input type="file" onChange={e => setFile(e.target.files?.[0])} accept=".pdf,.jpg,.jpeg,.png" className="block w-full text-sm" />
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="Numero DDT" className="input" value={form.numero} onChange={e => setForm({ ...form, numero: e.target.value })} />
            <input type="date" className="input" value={form.data} onChange={e => setForm({ ...form, data: e.target.value })} />
            <input placeholder="Fornitore" className="input col-span-2" value={form.fornitore_label} onChange={e => setForm({ ...form, fornitore_label: e.target.value })} />
            <input type="number" step="0.01" placeholder="Totale (opzionale)" className="input col-span-2" value={form.totale} onChange={e => setForm({ ...form, totale: e.target.value })} />
          </div>
          <textarea placeholder="Note" rows={2} className="input w-full" value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />
          <div className="flex justify-end gap-2">
            <button onClick={() => { setShowForm(false); setFile(null); }} className="px-3 py-1.5 border rounded text-sm">Annulla</button>
            <button onClick={submit} disabled={uploading} className="px-4 py-1.5 bg-primary text-white rounded text-sm font-bold disabled:opacity-50">{uploading ? 'Upload...' : 'Carica'}</button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {items.map(it => (
          <div key={it.id} className="bg-white border rounded p-3 flex items-center gap-3">
            <FilePlus size={20} className="text-primary" />
            <div className="flex-1">
              <div className="flex items-center gap-2"><b>{it.numero || '(no numero)'}</b><span className="text-xs text-slate-500">{fmtDate(it.data)}</span></div>
              <p className="text-xs text-slate-500">{it.fornitore_label || '—'}{it.totale > 0 ? ` · ${fmt(it.totale)}` : ''}</p>
              {it.note && <p className="text-xs text-slate-400 italic">{it.note}</p>}
            </div>
            <a href={`${API_URL}${it.file_url}`} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:bg-blue-50 p-1.5 rounded"><Download size={14} /></a>
            <button onClick={() => del(it.id)} className="text-red-500 hover:bg-red-50 p-1.5 rounded"><Trash2 size={14} /></button>
          </div>
        ))}
        {items.length === 0 && <p className="text-slate-400 text-center py-6 text-sm">Nessun DDT caricato. Carica le bolle dei fornitori.</p>}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// DOCUMENTI TAB
// ════════════════════════════════════════════════════════════════
const DOC_CATEGORIE = [
  { v: 'cila_scia', l: 'CILA / SCIA' },
  { v: 'permessi', l: 'Permessi / Autorizzazioni' },
  { v: 'contratti', l: 'Contratti' },
  { v: 'disegni', l: 'Disegni / Progetti' },
  { v: 'foto', l: 'Foto cantiere' },
  { v: 'certificati', l: 'Certificati / Collaudi' },
  { v: 'sicurezza', l: 'Sicurezza / DPI' },
  { v: 'varianti', l: 'Varianti' },
  { v: 'altro', l: 'Altro' },
];

function DocumentiTab({ cid }) {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ nome: '', categoria: 'altro', descrizione: '', scadenza: '' });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [filtro, setFiltro] = useState('');

  const API_URL = BACKEND_URL;
  const load = useCallback(() => API.get(`/api/cantieri/${cid}/documenti`).then(r => setItems(r.data)), [cid]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!file) { alert('Seleziona file'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('nome', form.nome);
      fd.append('categoria', form.categoria);
      fd.append('descrizione', form.descrizione);
      if (form.scadenza) fd.append('scadenza', form.scadenza);
      await API.post(`/api/cantieri/${cid}/documenti`, fd);
      setShowForm(false); setFile(null); setForm({ nome: '', categoria: 'altro', descrizione: '', scadenza: '' });
      load();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
    setUploading(false);
  };
  const del = async (id) => { if (!await confirm({ title: 'Eliminare documento?', message: 'Il file verrà rimosso dai documenti di cantiere.', confirmLabel: 'Elimina', variant: 'danger' })) return; await API.delete(`/api/cantieri/${cid}/documenti/${id}`); load(); };
  const filtered = filtro ? items.filter(i => i.categoria === filtro) : items;

  return (
    <div data-testid="documenti-tab">
      <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
        <h3 className="font-bold flex items-center gap-2"><FileText size={16} /> Documenti ({items.length})</h3>
        <div className="flex gap-2">
          <select className="input" value={filtro} onChange={e => setFiltro(e.target.value)}>
            <option value="">Tutte le categorie</option>
            {DOC_CATEGORIE.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
          </select>
          <button onClick={() => setShowForm(true)} className="px-3 py-1.5 bg-primary text-white rounded text-sm font-bold flex items-center gap-1" data-testid="add-documento-btn"><Upload size={14} /> Carica</button>
        </div>
      </div>

      {showForm && (
        <div className="bg-slate-50 border rounded-md p-4 mb-3 space-y-3">
          <input type="file" onChange={e => setFile(e.target.files?.[0])} className="block w-full text-sm" />
          <input placeholder="Nome documento" className="input w-full" value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} />
          <select className="input w-full" value={form.categoria} onChange={e => setForm({ ...form, categoria: e.target.value })}>
            {DOC_CATEGORIE.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
          </select>
          <textarea placeholder="Descrizione" rows={2} className="input w-full" value={form.descrizione} onChange={e => setForm({ ...form, descrizione: e.target.value })} />
          <input type="date" placeholder="Scadenza" className="input w-full" value={form.scadenza} onChange={e => setForm({ ...form, scadenza: e.target.value })} />
          <div className="flex justify-end gap-2">
            <button onClick={() => { setShowForm(false); setFile(null); }} className="px-3 py-1.5 border rounded text-sm">Annulla</button>
            <button onClick={submit} disabled={uploading} className="px-4 py-1.5 bg-primary text-white rounded text-sm font-bold disabled:opacity-50">{uploading ? 'Upload...' : 'Carica'}</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {filtered.map(it => {
          const cat = DOC_CATEGORIE.find(c => c.v === it.categoria);
          const expired = it.scadenza && new Date(it.scadenza) < new Date();
          return (
            <div key={it.id} className="bg-white border rounded p-3 flex items-start gap-3">
              <FileText size={20} className="text-primary mt-1" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <b className="text-sm truncate">{it.nome}</b>
                  <span className="text-[10px] bg-slate-100 px-2 py-0.5 rounded font-bold">{cat?.l || it.categoria}</span>
                  {expired && <span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded font-bold">SCADUTO</span>}
                </div>
                <p className="text-xs text-slate-500 truncate">{it.descrizione || it.filename_originale}</p>
                <p className="text-[10px] text-slate-400">{fmtDate(it.uploaded_at)}{it.scadenza ? ` · scade ${fmtDate(it.scadenza)}` : ''}</p>
              </div>
              <a href={`${API_URL}${it.file_url}`} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:bg-blue-50 p-1.5 rounded"><Download size={14} /></a>
              <button onClick={() => del(it.id)} className="text-red-500 hover:bg-red-50 p-1.5 rounded"><Trash2 size={14} /></button>
            </div>
          );
        })}
        {filtered.length === 0 && <p className="col-span-full text-slate-400 text-center py-6 text-sm">Nessun documento.</p>}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// LAVORAZIONI EXTRA TAB
// ════════════════════════════════════════════════════════════════
const STATI_CANTIERE = [
  { v: 'da_avviare', label: 'Da avviare', cls: 'bg-slate-100 text-slate-700' },
  { v: 'in_corso',   label: 'In corso',   cls: 'bg-blue-100 text-blue-700' },
  { v: 'sospeso',    label: 'Sospeso',    cls: 'bg-amber-100 text-amber-700' },
  { v: 'completato', label: 'Completato', cls: 'bg-emerald-100 text-emerald-700' },
  { v: 'annullato',  label: 'Annullato',  cls: 'bg-rose-100 text-rose-700' },
];

function StatoCantiereSelect({ cid, cantiere, onChanged }) {
  const [busy, setBusy] = useState(false);
  const current = STATI_CANTIERE.find(s => s.v === cantiere?.stato) || STATI_CANTIERE[0];
  const change = async (e) => {
    const v = e.target.value;
    if (v === cantiere?.stato) return;
    setBusy(true);
    try {
      await API.put(`/api/cantieri/${cid}`, { stato: v });
      onChanged?.();
    } catch (err) {
      alert('Errore: ' + (err.response?.data?.detail || err.message));
    } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col items-start">
      <span className="text-[10px] uppercase font-bold text-slate-400 mb-0.5">Stato cantiere</span>
      <select value={cantiere?.stato || 'da_avviare'} onChange={change} disabled={busy}
        data-testid="cantiere-stato-select"
        className={`px-3 py-1.5 rounded text-sm font-bold border-0 cursor-pointer ${current.cls} ${busy ? 'opacity-50' : ''}`}>
        {STATI_CANTIERE.map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
      </select>
    </div>
  );
}

function CreaFatturaBtn({ cid, cantiere }) {
  const [showModal, setShowModal] = useState(false);
  if (!cantiere) return null;
  const tipoLabel = cantiere?.stato === 'completato' ? 'Fattura finale' : 'Crea Fattura';
  return (
    <>
      <button onClick={() => setShowModal(true)} data-testid="crea-fattura-cantiere-btn"
        className="px-3 py-2 bg-green-600 text-white text-xs font-bold rounded hover:bg-green-700 flex items-center gap-1">
        <Euro size={14} /> {tipoLabel}
      </button>
      {showModal && (
        <CreaFatturaModal cid={cid} cantiere={cantiere} onClose={() => setShowModal(false)} />
      )}
    </>
  );
}

function CreaFatturaModal({ cid, cantiere, onClose }) {
  const [tipoFattura, setTipoFattura] = useState(''); // ''=riepilogativa, 'sal', 'acconto', 'saldo_finale'
  const [pagatoreId, setPagatoreId] = useState('');
  const [salId, setSalId] = useState('');
  const [accontoMode, setAccontoMode] = useState('importo'); // 'importo' | 'percentuale'
  const [importoAcconto, setImportoAcconto] = useState('');
  const [percAcconto, setPercAcconto] = useState('30');
  const [descrizione, setDescrizione] = useState('');
  const [tipoDoc, setTipoDoc] = useState('fattura'); // fattura|proforma
  const [includeExtra, setIncludeExtra] = useState(true);
  const [clienti, setClienti] = useState([]);
  const [sals, setSals] = useState([]);
  const [riepilogo, setRiepilogo] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    API.get(`/api/cantieri/${cid}/clienti`).then(r => {
      const cl = r.data?.clienti || [];
      setClienti(cl);
      if (cl[0]) setPagatoreId(cl[0].id);
    });
    API.get(`/api/cantieri/${cid}/sal`).then(r => setSals((r.data || []).filter(s => s.stato === 'approvato')));
    API.get(`/api/cantieri/${cid}/riepilogo-fatturazione`).then(r => setRiepilogo(r.data)).catch(() => {});
  }, [cid]);

  const pagatoreRiepilogo = riepilogo?.per_pagatore?.find(p => p.cliente_id === pagatoreId);
  const multiCliente = clienti.length >= 2;
  // SAL filtrati per il pagatore selezionato (più gli unfiltered)
  const salsCompatibili = sals.filter(s => !s.pagatore_id || s.pagatore_id === pagatoreId);

  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        include_extra: includeExtra,
        tipo: tipoDoc,
        pagatore_id: pagatoreId || undefined,
      };
      if (tipoFattura) body.tipo_fattura = tipoFattura;
      if (tipoFattura === 'sal') {
        if (!salId) { alert('Seleziona un SAL da fatturare'); setBusy(false); return; }
        body.sal_id = salId;
      } else if (tipoFattura === 'acconto') {
        if (accontoMode === 'importo') {
          const v = parseFloat(importoAcconto);
          if (!v || v <= 0) { alert('Inserisci un importo acconto valido'); setBusy(false); return; }
          body.importo_acconto = v;
        } else {
          const v = parseFloat(percAcconto);
          if (!v || v <= 0 || v > 100) { alert('Percentuale acconto deve essere tra 0 e 100'); setBusy(false); return; }
          body.percentuale_acconto = v;
        }
        if (descrizione) body.descrizione_acconto = descrizione;
      } else if (tipoFattura === 'saldo_finale') {
        if (descrizione) body.descrizione_saldo = descrizione;
      }
      const { data } = await API.post(`/api/fatture/da-cantiere/${cid}`, body);
      alert(`Fattura ${data.numero_fattura} creata.\nVai in Contabilità → Fatture per completarla e inviarla.`);
      onClose();
    } catch (e) {
      alert('Errore: ' + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="crea-fattura-modal">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col shadow-2xl">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold flex items-center gap-2"><Euro size={18}/>Crea Fattura dal Cantiere</h3>
            <p className="text-xs text-slate-500 mt-0.5">Cantiere: {cantiere.codice_cantiere} - {cantiere.titolo}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-800"><X size={20}/></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Step 1: Pagatore */}
          {multiCliente && (
            <div>
              <label className="text-xs font-bold text-slate-600 uppercase tracking-wide block mb-1.5">1. Destinatario / Pagatore</label>
              <select value={pagatoreId} onChange={e => setPagatoreId(e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-300 rounded focus:ring-2 focus:ring-primary/40"
                data-testid="fattura-pagatore-select">
                {clienti.map((c, idx) => (
                  <option key={c.id} value={c.id}>{idx === 0 ? '★ ' : ''}{c.label}{idx === 0 ? ' (principale)' : ''}</option>
                ))}
              </select>
            </div>
          )}

          {/* Riepilogo del pagatore selezionato */}
          {pagatoreRiepilogo && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs">
              <p className="font-bold text-slate-700 mb-2">Situazione fatturazione <span className="text-primary">{pagatoreRiepilogo.cliente_label}</span></p>
              <div className="grid grid-cols-4 gap-2 text-center">
                <div><p className="text-slate-400 text-[10px] uppercase">Dovuto</p><p className="font-bold font-mono">{pagatoreRiepilogo.dovuto.toLocaleString('it-IT', {minimumFractionDigits: 2})} €</p></div>
                <div><p className="text-slate-400 text-[10px] uppercase">Fatturato</p><p className="font-bold font-mono text-blue-700">{pagatoreRiepilogo.fatturato.toLocaleString('it-IT', {minimumFractionDigits: 2})} €</p></div>
                <div><p className="text-slate-400 text-[10px] uppercase">Residuo</p><p className="font-bold font-mono text-green-700">{pagatoreRiepilogo.residuo.toLocaleString('it-IT', {minimumFractionDigits: 2})} €</p></div>
                <div><p className="text-slate-400 text-[10px] uppercase">%</p><p className="font-bold font-mono">{pagatoreRiepilogo.perc_fatturato}%</p></div>
              </div>
              {pagatoreRiepilogo.fuori_contabilita > 0 && (
                <p className="text-[10px] text-red-600 mt-2 italic">⚠ Fuori contabilità: {pagatoreRiepilogo.fuori_contabilita.toLocaleString('it-IT', {minimumFractionDigits: 2})} € (non fatturato)</p>
              )}
            </div>
          )}

          {/* Step 2: Tipo fattura */}
          <div>
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wide block mb-1.5">{multiCliente ? '2. ' : ''}Tipo di fattura</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: '', label: 'Riepilogativa', desc: 'Ultimo SAL + extra (default)' },
                { v: 'acconto', label: 'Acconto', desc: 'Importo libero o %' },
                { v: 'sal', label: 'Da SAL specifico', desc: 'Fattura un SAL preciso' },
                { v: 'saldo_finale', label: 'Saldo Finale', desc: 'Residuo non fatturato' },
              ].map(t => (
                <button key={t.v} type="button" onClick={() => setTipoFattura(t.v)}
                  className={`text-left p-3 rounded border-2 transition-colors ${tipoFattura === t.v ? 'border-primary bg-primary' : 'border-slate-200 hover:border-slate-300'}`}
                  data-testid={`fattura-tipo-${t.v || 'riepilogativa'}`}>
                  <p className="text-sm font-bold">{t.label}</p>
                  <p className="text-[10px] text-slate-500">{t.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Step 3: parametri condizionali */}
          {tipoFattura === 'acconto' && (
            <div className="bg-primary border border-primary rounded p-3 space-y-2">
              <p className="text-xs font-bold text-primary">Parametri Acconto</p>
              <div className="flex gap-2">
                <label className="flex items-center gap-1 text-xs">
                  <input type="radio" checked={accontoMode === 'importo'} onChange={() => setAccontoMode('importo')} data-testid="acconto-mode-importo"/>
                  Importo libero
                </label>
                <label className="flex items-center gap-1 text-xs">
                  <input type="radio" checked={accontoMode === 'percentuale'} onChange={() => setAccontoMode('percentuale')} data-testid="acconto-mode-percentuale"/>
                  Percentuale sul preventivato
                </label>
              </div>
              {accontoMode === 'importo' ? (
                <input type="number" step="0.01" placeholder="es. 5000.00" value={importoAcconto} onChange={e => setImportoAcconto(e.target.value)}
                  className="w-full px-3 py-2 border border-primary rounded text-sm" data-testid="acconto-importo"/>
              ) : (
                <div className="flex items-center gap-2">
                  <input type="number" step="0.1" min="0" max="100" placeholder="30" value={percAcconto} onChange={e => setPercAcconto(e.target.value)}
                    className="w-24 px-3 py-2 border border-primary rounded text-sm" data-testid="acconto-percentuale"/>
                  <span className="text-sm text-slate-600">% del preventivato del pagatore</span>
                  {pagatoreRiepilogo && (
                    <span className="text-xs text-slate-500 ml-auto">
                      ≈ <b className="text-primary">{(pagatoreRiepilogo.dovuto * parseFloat(percAcconto || 0) / 100).toLocaleString('it-IT', {minimumFractionDigits: 2})} €</b>
                    </span>
                  )}
                </div>
              )}
              <input placeholder="Descrizione personalizzata (opzionale)" value={descrizione} onChange={e => setDescrizione(e.target.value)}
                className="w-full px-3 py-1.5 border border-primary rounded text-xs"/>
            </div>
          )}

          {tipoFattura === 'sal' && (
            <div className="bg-blue-50 border border-blue-200 rounded p-3 space-y-2">
              <p className="text-xs font-bold text-blue-700">Seleziona SAL approvato</p>
              {salsCompatibili.length === 0 ? (
                <p className="text-xs text-slate-500 italic">Nessun SAL approvato disponibile{multiCliente ? ' per questo pagatore' : ''}. Crea e approva prima un SAL.</p>
              ) : (
                <select value={salId} onChange={e => setSalId(e.target.value)}
                  className="w-full px-3 py-2 border border-blue-300 rounded text-sm" data-testid="fattura-sal-select">
                  <option value="">-- Scegli SAL --</option>
                  {salsCompatibili.map(s => (
                    <option key={s.id} value={s.id}>{s.codice_sal} - €{(s.totale_periodo || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})}{s.pagatore_label ? ` (${s.pagatore_label})` : ''}</option>
                  ))}
                </select>
              )}
            </div>
          )}

          {tipoFattura === 'saldo_finale' && (
            <div className="bg-green-50 border border-green-200 rounded p-3 space-y-2">
              <p className="text-xs font-bold text-green-700">Fattura di Saldo Finale</p>
              <p className="text-xs text-slate-600">Verrà fatturato il <b>residuo</b> non ancora fatturato per questo pagatore:
                {pagatoreRiepilogo && <span className="block mt-1">Dovuto <b>{pagatoreRiepilogo.dovuto.toFixed(2)} €</b> - Già fatturato <b>{pagatoreRiepilogo.fatturato.toFixed(2)} €</b> = <b className="text-green-700">{pagatoreRiepilogo.residuo.toFixed(2)} €</b></span>}
              </p>
              <input placeholder="Descrizione personalizzata (opzionale)" value={descrizione} onChange={e => setDescrizione(e.target.value)}
                className="w-full px-3 py-1.5 border border-green-200 rounded text-xs"/>
            </div>
          )}

          {/* Tipo documento e include extra */}
          <div className="flex items-center gap-4 pt-3 border-t border-slate-100">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-600">Documento:</span>
              <select value={tipoDoc} onChange={e => setTipoDoc(e.target.value)}
                className="px-2 py-1 border border-slate-300 rounded text-xs" data-testid="fattura-tipo-doc">
                <option value="fattura">Fattura</option>
                <option value="proforma">Proforma</option>
              </select>
            </div>
            {(tipoFattura === '' || tipoFattura === 'acconto' || tipoFattura === 'saldo_finale') && (
              <label className="flex items-center gap-1 text-xs">
                <input type="checkbox" checked={includeExtra} onChange={e => setIncludeExtra(e.target.checked)} data-testid="fattura-include-extra"/>
                Includi lavorazioni extra
              </label>
            )}
          </div>
        </div>

        <div className="p-4 border-t border-slate-200 flex gap-2 justify-end bg-slate-50 rounded-b-lg">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded" data-testid="fattura-cancel">Annulla</button>
          <button onClick={submit} disabled={busy}
            className="px-5 py-2 text-sm bg-green-600 text-white font-bold rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
            data-testid="fattura-submit">
            <Euro size={14}/> {busy ? 'Creazione…' : 'Crea Fattura'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ExtraTab({ cid, cantiere, onSaved }) {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const empty = { descrizione: '', richiesta_da: 'cliente', stato: 'proposta', modalita: 'consuntivo', importo_stimato: 0, importo_finale: 0, note: '' };
  const [form, setForm] = useState(empty);
  const [consuntivoFor, setConsuntivoFor] = useState(null);
  const [consuntivoForm, setConsuntivoForm] = useState({ importo_finale: 0, note: '' });

  const load = useCallback(() => API.get(`/api/cantieri/${cid}/extra`).then(r => setItems(r.data)), [cid]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditingId(null); setForm(empty); setShowForm(true); };
  const openEdit = (it) => {
    setEditingId(it.id);
    setForm({
      descrizione: it.descrizione || '',
      richiesta_da: it.richiesta_da || 'cliente',
      stato: it.stato || 'proposta',
      modalita: it.modalita || 'consuntivo',
      importo_stimato: it.importo_stimato || 0,
      importo_finale: it.importo_finale || 0,
      note: it.note || '',
    });
    setShowForm(true);
  };

  const submit = async () => {
    try {
      if (editingId) {
        await API.put(`/api/cantieri/${cid}/extra/${editingId}`, form);
      } else {
        await API.post(`/api/cantieri/${cid}/extra`, form);
      }
      setShowForm(false); setEditingId(null); setForm(empty); load(); onSaved?.();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const updateStato = async (id, stato) => { await API.put(`/api/cantieri/${cid}/extra/${id}`, { stato }); load(); onSaved?.(); };
  const del = async (id) => { if (!await confirm({ title: 'Eliminare lavorazione extra?', message: 'La lavorazione fuori preventivo verrà rimossa.', confirmLabel: 'Elimina', variant: 'danger' })) return; await API.delete(`/api/cantieri/${cid}/extra/${id}`); load(); onSaved?.(); };

  const openConsuntiva = (it) => {
    setConsuntivoFor(it);
    setConsuntivoForm({ importo_finale: it.importo_stimato || 0, note: '' });
  };
  const confermaConsuntivo = async () => {
    if (!consuntivoFor) return;
    const imp = parseFloat(consuntivoForm.importo_finale);
    if (!imp || imp <= 0) { alert('Importo finale obbligatorio'); return; }
    try {
      await API.post(`/api/cantieri/${cid}/extra/${consuntivoFor.id}/consuntiva`, consuntivoForm);
      setConsuntivoFor(null);
      load(); onSaved?.();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const STATO_COLOR = { proposta: 'bg-yellow-100 text-yellow-700', approvata: 'bg-blue-100 text-blue-700', rifiutata: 'bg-red-100 text-red-700', in_corso: 'bg-purple-100 text-purple-700', completata: 'bg-green-100 text-green-700' };
  const tot = items.reduce((s, x) => s + (x.importo_finale || x.importo_stimato || 0), 0);
  const totConsuntivato = items.filter(x => x.stato === 'completata' && x.importo_finale > 0).reduce((s, x) => s + x.importo_finale, 0);

  return (
    <div data-testid="extra-tab">
      <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
        <h3 className="font-bold flex items-center gap-2"><Sparkles size={16} /> Lavorazioni Extra ({items.length})</h3>
        <div className="flex items-center gap-3 text-xs">
          <span>Stimato: <b>{fmt(tot)}</b></span>
          <span className="text-green-700">Consuntivato: <b>{fmt(totConsuntivato)}</b></span>
          <button onClick={openNew} className="px-3 py-1.5 bg-primary text-white rounded text-sm font-bold flex items-center gap-1" data-testid="add-extra-btn"><Plus size={14} /> Nuova</button>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-3 text-xs text-blue-900">
        Registra le lavorazioni richieste in corso d'opera. Quando il lavoro è finito clicca <b>"Consuntiva"</b> per fissare l'importo: la voce entrerà nelle statistiche del cantiere e nei prossimi SAL.
      </div>

      {showForm && (
        <div className="bg-slate-50 border rounded-md p-4 mb-3 space-y-3" data-testid="extra-form">
          <div className="flex items-center justify-between">
            <h4 className="font-bold text-sm flex items-center gap-2">
              {editingId ? <><Edit2 size={14}/> Modifica lavorazione extra</> : <><Plus size={14}/> Nuova lavorazione extra</>}
            </h4>
            {editingId && <span className="text-[10px] bg-amber-100 text-amber-800 px-2 py-0.5 rounded font-bold">In modifica</span>}
          </div>
          <textarea placeholder="Descrizione della lavorazione richiesta..." rows={3} className="input w-full" value={form.descrizione} onChange={e => setForm({ ...form, descrizione: e.target.value })} data-testid="extra-descrizione" />
          <div className="grid grid-cols-2 gap-2">
            <select className="input" value={form.richiesta_da} onChange={e => setForm({ ...form, richiesta_da: e.target.value })}>
              <option value="cliente">Cliente</option>
              <option value="tecnico">Tecnico/DL</option>
              <option value="committente">Committente</option>
            </select>
            <select className="input" value={form.modalita} onChange={e => setForm({ ...form, modalita: e.target.value })}>
              <option value="consuntivo">Consuntivo (a fine lavori)</option>
              <option value="preventivo_separato">Nuovo preventivo separato</option>
            </select>
            <select className="input col-span-2" value={form.stato} onChange={e => setForm({ ...form, stato: e.target.value })}>
              <option value="proposta">Proposta</option>
              <option value="approvata">Approvata</option>
              <option value="rifiutata">Rifiutata</option>
              <option value="in_corso">In corso</option>
              <option value="completata">Completata</option>
            </select>
            <div className="col-span-2 grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500">Importo stimato (EUR)</label>
                <input type="number" step="0.01" placeholder="0.00" className="input w-full" value={form.importo_stimato} onChange={e => setForm({ ...form, importo_stimato: e.target.value })} data-testid="extra-importo-stimato" />
              </div>
              {editingId && (
                <div>
                  <label className="text-[10px] font-bold uppercase text-slate-500">Importo finale (EUR)</label>
                  <input type="number" step="0.01" placeholder="0.00" className="input w-full" value={form.importo_finale} onChange={e => setForm({ ...form, importo_finale: e.target.value })} data-testid="extra-importo-finale" />
                </div>
              )}
            </div>
          </div>
          <textarea placeholder="Note" rows={2} className="input w-full" value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />
          {editingId && form.importo_finale > 0 && (
            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 p-2 rounded">
              ⚠ Stai modificando l'importo <b>finale (consuntivato)</b>: verra' aggiornato anche nelle statistiche del cantiere e nei prossimi SAL. I SAL gia' approvati restano invariati.
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button onClick={() => { setShowForm(false); setEditingId(null); }} className="px-3 py-1.5 border rounded text-sm">Annulla</button>
            <button onClick={submit} className="px-4 py-1.5 bg-primary text-white rounded text-sm font-bold" data-testid="extra-save-btn">
              {editingId ? 'Salva Modifiche' : 'Salva'}
            </button>
          </div>
        </div>
      )}

      {consuntivoFor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5 space-y-3">
            <h3 className="text-lg font-bold flex items-center gap-2"><CheckCircle2 size={18} className="text-green-600" /> Consuntiva lavorazione</h3>
            <p className="text-sm text-slate-600">{consuntivoFor.descrizione}</p>
            <div>
              <label className="text-xs font-bold uppercase text-slate-600">Importo finale (€)</label>
              <input autoFocus type="number" step="0.01" min="0" className="input w-full"
                value={consuntivoForm.importo_finale}
                onChange={e => setConsuntivoForm(f => ({ ...f, importo_finale: e.target.value }))}
                data-testid="consuntivo-importo" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-slate-600">Note consuntivo (opzionale)</label>
              <textarea rows={2} className="input w-full" value={consuntivoForm.note}
                onChange={e => setConsuntivoForm(f => ({ ...f, note: e.target.value }))} />
            </div>
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-2 rounded">
              Una volta consuntivato, l'importo entra nelle statistiche del cantiere e sarà incluso nei prossimi SAL/fattura.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConsuntivoFor(null)} className="px-3 py-1.5 border rounded text-sm">Annulla</button>
              <button onClick={confermaConsuntivo} className="px-4 py-1.5 bg-green-600 text-white rounded text-sm font-bold" data-testid="confirm-consuntivo-btn">Conferma consuntivo</button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {items.map(it => (
          <div key={it.id} className="bg-white border rounded p-3">
            <div className="flex items-start gap-3">
              <Sparkles size={18} className="text-purple-500 mt-1" />
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${STATO_COLOR[it.stato] || ''}`}>{it.stato}</span>
                  <span className="text-[10px] bg-slate-100 px-2 py-0.5 rounded">{it.modalita === 'preventivo_separato' ? 'Prev. separato' : 'Consuntivo'}</span>
                  <span className="text-[10px] text-slate-500">da {it.richiesta_da}</span>
                  <span className="text-[10px] text-slate-500">{fmtDate(it.data_richiesta)}</span>
                  {it.stato === 'completata' && it.importo_finale > 0 && (
                    <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded font-bold">✓ Consuntivato</span>
                  )}
                </div>
                <p className="text-sm">{it.descrizione}</p>
                {it.note && <p className="text-xs text-slate-500 italic mt-1">{it.note}</p>}
                {(it.importo_stimato > 0 || it.importo_finale > 0) && (
                  <p className="text-sm font-bold text-primary mt-1">
                    {it.importo_finale > 0
                      ? <>Finale: {fmt(it.importo_finale)}</>
                      : <>Stimato: {fmt(it.importo_stimato)}</>}
                  </p>
                )}
                {it.note_consuntivo && <p className="text-xs text-slate-500 mt-1">Note consuntivo: {it.note_consuntivo}</p>}
              </div>
              <div className="flex flex-col gap-1 items-end">
                {it.stato !== 'completata' && (
                  <button onClick={() => openConsuntiva(it)} className="text-[11px] px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-1 font-bold"
                    data-testid={`consuntiva-btn-${it.id}`}>
                    <CheckCircle2 size={12} /> Consuntiva
                  </button>
                )}
                <select value={it.stato} onChange={(e) => updateStato(it.id, e.target.value)} className="text-[10px] border rounded px-1 py-0.5">
                  <option value="proposta">Proposta</option>
                  <option value="approvata">Approvata</option>
                  <option value="rifiutata">Rifiutata</option>
                  <option value="in_corso">In corso</option>
                  <option value="completata">Completata</option>
                </select>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(it)} className="text-blue-600 hover:bg-blue-50 p-1 rounded" title="Modifica descrizione/importo" data-testid={`edit-extra-${it.id}`}>
                    <Edit2 size={12} />
                  </button>
                  <button onClick={() => del(it.id)} className="text-red-500 hover:bg-red-50 p-1 rounded" title="Elimina" data-testid={`delete-extra-${it.id}`}>
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-slate-400 text-center py-6 text-sm">Nessuna lavorazione extra registrata.</p>}
      </div>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════
// CLIENTI / PAGATORI MODAL — gestione multi-cliente del cantiere
// ════════════════════════════════════════════════════════════════
function ClientiCantiereModal({ cantiere, onClose, onSaved }) {
  const [clienti, setClienti] = useState([]);
  const [selectedIds, setSelectedIds] = useState(
    (cantiere.clienti_ids && cantiere.clienti_ids.length > 0)
      ? cantiere.clienti_ids
      : (cantiere.cliente_id ? [cantiere.cliente_id] : [])
  );
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    API.get('/api/clienti').then(r => setClienti(r.data || [])).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await API.put(`/api/cantieri/${cantiere.id || cantiere._id}`, { clienti_ids: selectedIds });
      onSaved?.();
    } catch (e) {
      alert('Errore: ' + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="clienti-cantiere-modal">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col shadow-2xl">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold">Clienti / Pagatori del cantiere</h3>
            <p className="text-xs text-slate-500 mt-0.5">Il primo cliente è il principale. Gli altri possono pagare voci specifiche del preventivo/SAL.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-800"><X size={20} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          <MultiClienteSelect
            value={selectedIds}
            onChange={setSelectedIds}
            clienti={clienti}
            placeholder="Aggiungi almeno un cliente"
            testid="cantiere-clienti"
          />
          <p className="text-[11px] text-slate-400 italic mt-3">
            ℹ️ Se modifichi i clienti, i SAL e le fatture future erediteranno automaticamente questa lista. Le righe del preventivo già attribuite restano invariate.
          </p>
        </div>
        <div className="p-4 border-t border-slate-200 flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded" data-testid="cantiere-clienti-cancel">Annulla</button>
          <button onClick={save} disabled={busy || selectedIds.length === 0}
            className="px-4 py-2 text-sm bg-primary text-white font-bold rounded hover:bg-primary-hover disabled:opacity-50"
            data-testid="cantiere-clienti-save">
            {busy ? 'Salvataggio…' : 'Salva'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════
// PAGATORI TAB — riepilogo fatturazione per ciascun pagatore
// ════════════════════════════════════════════════════════════════
function PagatoriTab({ cid, cantiere }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    API.get(`/api/cantieri/${cid}/riepilogo-fatturazione`)
      .then(r => { setData(r.data); setErr(''); })
      .catch(e => setErr(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [cid]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <p className="text-center text-slate-400 py-8">Caricamento riepilogo…</p>;
  if (err) return <p className="text-center text-red-500 py-8">Errore: {err}</p>;
  if (!data) return null;

  const fmt = (v) => Number(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const totali = data.totali || {};
  const pagatori = data.per_pagatore || [];

  const tipoFatturaBadge = {
    acconto: { label: 'ACCONTO', cls: 'bg-primary text-primary' },
    sal: { label: 'SAL', cls: 'bg-blue-100 text-blue-700' },
    saldo_finale: { label: 'SALDO', cls: 'bg-green-100 text-green-700' },
    riepilogativa: { label: 'RIEPIL.', cls: 'bg-slate-100 text-slate-700' },
  };
  const statoPagBadge = {
    pagata: { label: '✅ Pagata', cls: 'bg-green-100 text-green-700' },
    parziale: { label: '🟡 Parziale', cls: 'bg-yellow-100 text-yellow-700' },
    non_pagata: { label: '⏳ Da incassare', cls: 'bg-slate-100 text-slate-600' },
    scaduta: { label: '⚠ Scaduta', cls: 'bg-red-100 text-red-700' },
    annullata: { label: '❌ Annullata', cls: 'bg-slate-200 text-slate-500' },
  };

  return (
    <div data-testid="pagatori-tab" className="space-y-4">
      {/* Banner totali aggregati */}
      <div className="bg-gradient-to-r from-primary to-amber-50 border border-primary rounded-lg p-4">
        <h3 className="text-sm font-bold text-primary uppercase tracking-wide mb-3 flex items-center gap-1.5">
          <Euro size={14} /> Riepilogo Fatturazione Pagatori
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-[10px] text-slate-500 uppercase font-bold">Totale Dovuto</p>
            <p className="text-xl font-bold font-mono text-slate-800">{fmt(totali.dovuto)} €</p>
          </div>
          <div>
            <p className="text-[10px] text-slate-500 uppercase font-bold">Fatturato</p>
            <p className="text-xl font-bold font-mono text-blue-700">{fmt(totali.fatturato)} €</p>
            <p className="text-[10px] text-slate-400">{totali.perc_fatturato}% del dovuto</p>
          </div>
          <div>
            <p className="text-[10px] text-slate-500 uppercase font-bold">Residuo</p>
            <p className="text-xl font-bold font-mono text-green-700">{fmt(totali.residuo)} €</p>
          </div>
          <div>
            <p className="text-[10px] text-slate-500 uppercase font-bold">Fuori Contabilità</p>
            <p className="text-xl font-bold font-mono text-red-700">{fmt(totali.fuori_contabilita)} €</p>
            <p className="text-[10px] text-slate-400 italic">non fatturato da EdilCima</p>
          </div>
        </div>
      </div>

      {/* Card per ogni pagatore */}
      {pagatori.length === 0 && (
        <p className="text-center text-slate-400 py-8 text-sm">Nessun pagatore associato al cantiere.</p>
      )}
      {pagatori.map(p => {
        const percColor = p.perc_fatturato >= 100 ? 'bg-green-500'
          : p.perc_fatturato >= 50 ? 'bg-blue-500'
          : p.perc_fatturato > 0 ? 'bg-primary'
          : 'bg-slate-300';
        return (
          <div key={p.cliente_id} className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`pagatore-card-${p.cliente_id}`}>
            {/* Header pagatore */}
            <div className={`p-4 border-b border-slate-100 ${p.is_primary ? 'bg-primary' : 'bg-slate-50'}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  {p.is_primary && <span className="text-[10px] font-bold text-primary bg-primary px-1.5 py-0.5 rounded">★ Principale</span>}
                  <h4 className="font-bold text-base">{p.cliente_label}</h4>
                  <span className="text-xs text-slate-500">{p.n_fatture} {p.n_fatture === 1 ? 'fattura' : 'fatture'} emesse</span>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-500">Dovuto · Fatturato · Residuo</p>
                  <p className="text-sm font-mono font-bold">
                    {fmt(p.dovuto)} · <span className="text-blue-700">{fmt(p.fatturato)}</span> · <span className="text-green-700">{fmt(p.residuo)} €</span>
                  </p>
                </div>
              </div>
              {/* Progress bar */}
              <div className="mt-3">
                <div className="flex items-center justify-between mb-1 text-[10px] text-slate-500">
                  <span>Avanzamento fatturazione</span>
                  <span className="font-bold">{p.perc_fatturato}%</span>
                </div>
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div className={`h-full transition-all ${percColor}`} style={{ width: `${Math.min(p.perc_fatturato, 100)}%` }} />
                </div>
              </div>
              {/* Sub-info */}
              <div className="mt-2 flex gap-3 text-[11px] text-slate-500 flex-wrap">
                <span>Preventivato <b className="text-slate-700">{fmt(p.preventivato)} €</b></span>
                {p.extra > 0 && <span>+ Extra <b className="text-slate-700">{fmt(p.extra)} €</b></span>}
                {p.fuori_contabilita > 0 && <span className="text-red-600">Fuori contabilità: <b>{fmt(p.fuori_contabilita)} €</b> (non fatturato)</span>}
                {p.pagato > 0 && <span>Incassato <b className="text-green-700">{fmt(p.pagato)} €</b></span>}
              </div>
            </div>

            {/* Lista fatture del pagatore */}
            {p.fatture && p.fatture.length > 0 ? (
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr className="text-slate-500 text-left">
                    <th className="px-3 py-2">Numero</th>
                    <th className="px-3 py-2">Tipo</th>
                    <th className="px-3 py-2">Data</th>
                    <th className="px-3 py-2 text-right">Imponibile</th>
                    <th className="px-3 py-2 text-right">Totale</th>
                    <th className="px-3 py-2 text-center">Stato</th>
                  </tr>
                </thead>
                <tbody>
                  {p.fatture.map(f => {
                    const tipoBg = tipoFatturaBadge[f.tipo_fattura] || tipoFatturaBadge.riepilogativa;
                    const statBg = statoPagBadge[f.stato_pagamento] || { label: f.stato_pagamento, cls: 'bg-slate-100 text-slate-600' };
                    return (
                      <tr key={f.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`pagatore-fattura-${f.id}`}>
                        <td className="px-3 py-2 font-bold font-mono">{f.numero_fattura}</td>
                        <td className="px-3 py-2"><span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${tipoBg.cls}`}>{tipoBg.label}</span></td>
                        <td className="px-3 py-2 text-slate-600">{f.data_emissione ? new Date(f.data_emissione).toLocaleDateString('it-IT') : '-'}</td>
                        <td className="px-3 py-2 text-right font-mono">{fmt(f.totale_imponibile)} €</td>
                        <td className="px-3 py-2 text-right font-mono font-bold">{fmt(f.totale_finale)} €</td>
                        <td className="px-3 py-2 text-center"><span className={`px-1.5 py-0.5 rounded text-[10px] font-bold whitespace-nowrap ${statBg.cls}`}>{statBg.label}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <p className="text-center text-slate-400 py-4 text-xs italic">Nessuna fattura emessa per questo pagatore</p>
            )}

            {p.residuo > 0 && (
              <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 text-[11px] text-slate-600 flex items-center justify-between">
                <span>💡 Residuo da fatturare: <b className="text-green-700">{fmt(p.residuo)} €</b></span>
                {p.perc_fatturato === 0 && <span className="italic">Suggerimento: emetti un <b>acconto</b> per iniziare</span>}
                {p.perc_fatturato > 0 && p.perc_fatturato < 100 && <span className="italic">Suggerimento: emetti una <b>fattura da SAL</b> o un <b>saldo finale</b></span>}
              </div>
            )}
          </div>
        );
      })}

      <p className="text-[10px] text-slate-400 italic text-center pt-2">
        ℹ️ Per creare una nuova fattura per un pagatore, usa il bottone <b>"Crea Fattura"</b> nell'header del cantiere.
      </p>
    </div>
  );
}
